/**
 * Structured-data audit rules — shared by the CI Playwright audit
 * (tests/e2e/structured-data.spec.ts) and the admin SEO Monitoring tab.
 *
 * Pure and DOM-agnostic apart from `extractJsonLd`, which reads a Document.
 */

export const CANONICAL_ORIGIN = "https://elarahealth.life";

export interface RouteContract {
  path: string;
  /** Expect a WebPage-family node (WebPage/AboutPage/ContactPage/MedicalWebPage/...) */
  webPage: boolean;
  /** Expect a BreadcrumbList (content routes below the homepage) */
  breadcrumbs: boolean;
  /** Expect an Article node */
  article?: boolean;
  /** Node @types that must be present */
  requiredTypes?: string[];
}

export const ROUTE_CONTRACTS: RouteContract[] = [
  {
    path: "/",
    webPage: true,
    breadcrumbs: false,
    requiredTypes: ["Organization", "WebSite", "MedicalBusiness"],
  },
  { path: "/about", webPage: true, breadcrumbs: true, requiredTypes: ["AboutPage", "FAQPage"] },
  { path: "/contact", webPage: true, breadcrumbs: true, requiredTypes: ["ContactPage"] },
  {
    path: "/peptides",
    webPage: true,
    breadcrumbs: true,
    article: true,
    requiredTypes: ["MedicalWebPage", "FAQPage"],
  },
  {
    path: "/peptides/weight-loss",
    webPage: true,
    breadcrumbs: true,
    article: true,
    requiredTypes: ["MedicalWebPage", "FAQPage"],
  },
  { path: "/careers", webPage: true, breadcrumbs: false },
  { path: "/get-started", webPage: true, breadcrumbs: false },
  { path: "/privacy", webPage: true, breadcrumbs: false },
  { path: "/terms", webPage: true, breadcrumbs: false },
  { path: "/hipaa", webPage: true, breadcrumbs: false },
  { path: "/medical-disclaimer", webPage: true, breadcrumbs: false },
  { path: "/login", webPage: true, breadcrumbs: false },
  { path: "/join/provider", webPage: true, breadcrumbs: false },
  { path: "/join/sales", webPage: true, breadcrumbs: false },
  { path: "/join/pharmacy", webPage: true, breadcrumbs: false },
];

export const WEBPAGE_TYPES = [
  "WebPage",
  "AboutPage",
  "ContactPage",
  "CollectionPage",
  "CheckoutPage",
  "MedicalWebPage",
  "ItemPage",
  "FAQPage",
];

export type JsonLdNode = Record<string, unknown>;

export interface ExtractResult {
  nodes: JsonLdNode[];
  errors: string[];
  blockCount: number;
}

/** Parse every JSON-LD block in raw form, flattening @graph containers. */
export function parseJsonLdBlocks(blocks: string[]): ExtractResult {
  const nodes: JsonLdNode[] = [];
  const errors: string[] = [];

  blocks.forEach((raw, i) => {
    if (!raw.trim()) {
      errors.push(`block #${i} is empty`);
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      errors.push(`block #${i} is not valid JSON: ${(e as Error).message}`);
      return;
    }
    const roots = Array.isArray(parsed) ? parsed : [parsed];
    for (const root of roots) {
      if (!root || typeof root !== "object") {
        errors.push(`block #${i} root is not an object`);
        continue;
      }
      const obj = root as JsonLdNode;
      if (obj["@context"] !== "https://schema.org") {
        errors.push(`block #${i} missing "@context": "https://schema.org"`);
      }
      const graph = obj["@graph"];
      if (Array.isArray(graph)) {
        graph.forEach((n, j) => {
          if (!n || typeof n !== "object") errors.push(`block #${i} @graph[${j}] is not an object`);
          else nodes.push(n as JsonLdNode);
        });
      } else {
        nodes.push(obj);
      }
    }
  });

  return { nodes, errors, blockCount: blocks.length };
}

/** Read and parse all JSON-LD from a Document (same-origin iframe or window). */
export function extractJsonLd(doc: Document): ExtractResult {
  const blocks = Array.from(doc.querySelectorAll('script[type="application/ld+json"]')).map(
    (el) => el.textContent ?? "",
  );
  return parseJsonLdBlocks(blocks);
}

export const typeOf = (node: JsonLdNode): string[] => {
  const t = node["@type"];
  if (typeof t === "string") return [t];
  if (Array.isArray(t)) return t.filter((x): x is string => typeof x === "string");
  return [];
};

export const hasType = (nodes: JsonLdNode[], type: string) =>
  nodes.some((n) => typeOf(n).includes(type));

export const nodesOfType = (nodes: JsonLdNode[], type: string) =>
  nodes.filter((n) => typeOf(n).includes(type));

const isNonEmptyString = (v: unknown) => typeof v === "string" && v.trim().length > 0;

/**
 * Validate nodes against the rules Google's Rich Results Test enforces plus our
 * house rules. Returns human-readable problems (empty array = healthy).
 */
export function validateNodes(nodes: JsonLdNode[], contract: RouteContract): string[] {
  const problems: string[] = [];
  const expectedUrl =
    CANONICAL_ORIGIN + (contract.path === "/" ? "/" : contract.path.replace(/\/+$/, ""));

  if (nodes.length === 0) {
    problems.push("no JSON-LD nodes rendered");
    return problems;
  }

  nodes.forEach((n, i) => {
    if (typeOf(n).length === 0) problems.push(`node #${i} has no @type`);
  });

  const seen = new Map<string, number>();
  nodes.forEach((n) => {
    const id = n["@id"];
    if (typeof id === "string") seen.set(id, (seen.get(id) ?? 0) + 1);
  });
  for (const [id, count] of seen) {
    if (count > 1) problems.push(`duplicate @id rendered ${count}x: ${id}`);
  }

  for (const singleton of ["Organization", "WebSite", "BreadcrumbList", "Article", "FAQPage"]) {
    const count = nodesOfType(nodes, singleton).length;
    if (count > 1) problems.push(`duplicate ${singleton} node rendered ${count}x`);
  }

  const checkUrl = (label: string, value: unknown) => {
    if (value === undefined) return;
    if (typeof value !== "string") {
      problems.push(`${label} is not a string`);
      return;
    }
    if (!/^https:\/\//.test(value)) problems.push(`${label} is not an absolute https URL: ${value}`);
    else if (!value.startsWith(CANONICAL_ORIGIN)) {
      problems.push(`${label} does not use the canonical origin: ${value}`);
    }
  };

  const walk = (value: unknown, path: string) => {
    if (value === null) problems.push(`${path} is null`);
    else if (Array.isArray(value)) {
      if (value.length === 0) problems.push(`${path} is an empty array`);
      value.forEach((v, i) => walk(v, `${path}[${i}]`));
    } else if (typeof value === "object") {
      for (const [k, v] of Object.entries(value as JsonLdNode)) walk(v, `${path}.${k}`);
    } else if (typeof value === "string" && value.trim() === "") {
      problems.push(`${path} is an empty string`);
    } else if (typeof value === "string" && value.includes("undefined")) {
      problems.push(`${path} contains "undefined": ${value}`);
    }
  };
  nodes.forEach((n, i) => walk(n, `${typeOf(n)[0] ?? "node"}#${i}`));

  if (contract.webPage) {
    const pageNodes = nodes.filter((n) =>
      typeOf(n).some((t) => WEBPAGE_TYPES.includes(t) && t !== "FAQPage"),
    );
    if (pageNodes.length === 0) {
      problems.push("no WebPage-family node (WebPage/AboutPage/ContactPage/MedicalWebPage)");
    }
    for (const p of pageNodes) {
      const label = typeOf(p)[0];
      if (!isNonEmptyString(p.name)) problems.push(`${label} missing name`);
      if (!isNonEmptyString(p.description)) problems.push(`${label} missing description`);
      if (!p.mainEntityOfPage) problems.push(`${label} missing mainEntityOfPage`);
      checkUrl(`${label}.url`, p.url);
      checkUrl(`${label}.@id`, p["@id"]);
      if (typeof p.url === "string" && p.url !== expectedUrl) {
        problems.push(`${label}.url ${p.url} does not self-reference ${expectedUrl}`);
      }
    }
  }

  const crumbs = nodesOfType(nodes, "BreadcrumbList");
  if (contract.breadcrumbs && crumbs.length === 0) problems.push("missing BreadcrumbList");
  for (const crumb of crumbs) {
    const items = crumb.itemListElement;
    if (!Array.isArray(items) || items.length === 0) {
      problems.push("BreadcrumbList.itemListElement is missing or empty");
      continue;
    }
    items.forEach((raw, i) => {
      const item = raw as JsonLdNode;
      if (typeOf(item)[0] !== "ListItem") problems.push(`breadcrumb[${i}] is not a ListItem`);
      if (item.position !== i + 1) {
        problems.push(`breadcrumb[${i}] position is ${item.position}, expected ${i + 1}`);
      }
      if (!isNonEmptyString(item.name)) problems.push(`breadcrumb[${i}] missing name`);
      if (i < items.length - 1) {
        if (!item.item) problems.push(`breadcrumb[${i}] missing item URL`);
        else checkUrl(`breadcrumb[${i}].item`, item.item);
      }
    });
  }

  if (contract.article) {
    const articles = nodesOfType(nodes, "Article");
    if (articles.length === 0) problems.push("missing Article node");
    for (const a of articles) {
      for (const field of ["headline", "description", "datePublished", "dateModified"]) {
        if (!isNonEmptyString(a[field])) problems.push(`Article missing ${field}`);
      }
      if (typeof a.headline === "string" && a.headline.length > 110) {
        problems.push(`Article headline exceeds 110 chars (${a.headline.length})`);
      }
      for (const field of ["datePublished", "dateModified"]) {
        const v = a[field];
        if (typeof v === "string" && Number.isNaN(Date.parse(v))) {
          problems.push(`Article ${field} is not a valid date: ${v}`);
        }
      }
      if (!a.author) problems.push("Article missing author");
      if (!a.publisher) problems.push("Article missing publisher");
      if (!a.image) problems.push("Article missing image");
    }
  }

  for (const faq of nodesOfType(nodes, "FAQPage")) {
    const entities = faq.mainEntity;
    if (!Array.isArray(entities) || entities.length === 0) {
      problems.push("FAQPage.mainEntity is missing or empty");
      continue;
    }
    entities.forEach((raw, i) => {
      const q = raw as JsonLdNode;
      if (typeOf(q)[0] !== "Question") problems.push(`FAQ entity[${i}] is not a Question`);
      if (!isNonEmptyString(q.name)) problems.push(`FAQ entity[${i}] missing name`);
      const answer = q.acceptedAnswer as JsonLdNode | undefined;
      if (!answer || !isNonEmptyString(answer.text)) {
        problems.push(`FAQ entity[${i}] missing acceptedAnswer.text`);
      }
    });
  }

  for (const type of contract.requiredTypes ?? []) {
    if (!hasType(nodes, type)) problems.push(`missing required node type: ${type}`);
  }

  const declaredIds = new Set(
    nodes.map((n) => n["@id"]).filter((id): id is string => typeof id === "string"),
  );
  const sitewideIds = new Set([
    `${CANONICAL_ORIGIN}/#organization`,
    `${CANONICAL_ORIGIN}/#website`,
  ]);
  const checkRefs = (value: unknown, path: string) => {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) {
      value.forEach((v, i) => checkRefs(v, `${path}[${i}]`));
      return;
    }
    const obj = value as JsonLdNode;
    const keys = Object.keys(obj);
    const id = obj["@id"];
    if (keys.length === 1 && typeof id === "string" && !declaredIds.has(id) && !sitewideIds.has(id)) {
      problems.push(`${path} references unresolved @id: ${id}`);
    }
    for (const [k, v] of Object.entries(obj)) {
      if (k !== "@id") checkRefs(v, `${path}.${k}`);
    }
  };
  nodes.forEach((n, i) => checkRefs(n, `${typeOf(n)[0] ?? "node"}#${i}`));

  return problems;
}

export interface RouteAuditResult {
  path: string;
  status: "healthy" | "problems" | "error";
  problems: string[];
  nodeTypes: string[];
  blockCount: number;
}

/** Summarize a full audit run. */
export function summarizeAudit(results: RouteAuditResult[]) {
  return {
    total: results.length,
    healthy: results.filter((r) => r.status === "healthy").length,
    problems: results.filter((r) => r.status === "problems").length,
    errors: results.filter((r) => r.status === "error").length,
    problemCount: results.reduce((sum, r) => sum + r.problems.length, 0),
  };
}
